import TagIcon from "@mui/icons-material/Tag";
import TranslateIcon from "@mui/icons-material/Translate";
import {
    Chip,
    CircularProgress,
    Dialog,
    Grid,
    MenuItem,
    Stack,
    Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { APP_IMAGES } from "../../assets";
import { ECOMMERCE_SOLUTION_TYPE } from "../../constants/Variables";
import globalStyle from "../../globalStyle";
import { baseUrl } from "../../services";
import { createEcommerceTag, updateEcommerceTag } from "../../services/ecommerceTageApiFn";
import { capitalizeText, handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import { addEditEcommerceTagsValidation } from "../../utils/Validation";
import AppLoader from "../AppLoader";
import CustomDropDown from "../CutomDropDown";
import Label from "../Label";
import TextInput from "../TextInputs";
import UploadBox from "../UploadBox";
import DialogBody from "./Body";
import DialogActionButtons from "./Footer";
import DialogHeader from "./Header";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(4, 2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(2),
    },
    "& .MuiPaper-root": {
        width: "600px",
        [theme.breakpoints.down("sm")]: {
            margin: 12,
        },
    },
    "& .MuiInputBase-multiline": {
        padding: "6px !important",
    },
}));

let paramsData = {};

const initialInputValues = {
    icon: null,
    name_en: "",
    name_ro: "",
    description_en: "",
    description_ro: "",
    order: 0,
    solutionType: [],
    appliesTo: "package",
};

const AddEditEcommerceTagDialog = forwardRef(({ }, ref) => {
    const openIconFile = useRef(null);
    const { t } = useTranslation(["catalogue", "common"]);
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState({
        translateToEnglish: false,
        save: false,
        icon: false,
    });
    const [inputError, setInputError] = useState({});
    const [inputValues, setInputValues] = useState(initialInputValues);

    // Redux State
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    const { currentLanguage } = useSelector((state) => state.languageReducer);

    useImperativeHandle(ref, () => ({
        openDialog(params) {
            setOpen(true);
            paramsData = params;
            if (params.type === "edit") {
                const data = params.data;
                setInputValues({
                    icon: data?.icon || null,
                    name_en: capitalizeText(data?.name?.en) || "",
                    name_ro: capitalizeText(data?.name?.ro) || "",
                    description_en: data?.description?.en || "",
                    description_ro: data?.description?.ro || "",
                    order: data?.order || 0,
                    solutionType: data?.solutionType || [],
                    appliesTo: "package",
                });
            }
        },

        closeDialog() {
            handleClose();
        },
    }));

    const handleClose = () => {
        setOpen(false);
        setInputValues(initialInputValues);
        setInputError({});
    };

    const handleAddEdit = () => {
        const valid = addEditEcommerceTagsValidation(inputValues, setInputError);

        if (valid) {
            return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));
        }

        let payload = {
            icon: inputValues.icon,
            name: {
                en: inputValues.name_en,
                ro: inputValues.name_ro,
            },
            description: {
                en: inputValues.description_en,
                ro: inputValues.description_ro,
            },
            order: inputValues.order,
            solutionType: inputValues.solutionType,
            appliesTo: inputValues.appliesTo,
        };

        if (paramsData?.type === "add") {
            handleApiCall(createEcommerceTag, payload, "");
        } else {
            handleApiCall(updateEcommerceTag, payload, paramsData?.data?._id);
        }
    };

    const handleApiCall = async (apiFunction, payload, id) => {
        try {
            setIsLoading((prev) => ({ ...prev, save: true }));
            const response = await apiFunction(id, payload);
            if (response?.data?.status === "success") {
                handleOpenSnackBar("success", response?.data?.message);
                handleClose();
                paramsData?.getData(searchKeys.ecommerceTagName);
            } else {
                handleOpenSnackBar("error", response?.data?.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", t("error.somethingWentWrong", { ns: "common" }));
        } finally {
            setIsLoading((prev) => ({ ...prev, save: false }));
        }
    };

    const handleTranslateToEnglish = async (key1, key2) => {
        setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
        try {
            const [englishText] = await Promise.all([
                translateText(inputValues[key1]),
            ]);
            setInputValues((prev) => ({
                ...prev,
                [key2]: englishText,
            }));
            setInputError((prev) => ({
                ...prev,
                [key2]: "",
            }));
        } catch (error) {
            handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
        } finally {
            setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
        }
    };

    const handleChageInputs = (e) => {
        const { name, value, valueAsNumber, type } = e.target;
        const formatedValue = type === "number" ? valueAsNumber : value;

        setInputValues((pre) => ({ ...pre, [name]: formatedValue }));
        setInputError((pre) => ({ ...pre, [name]: "" }));
    };

    const handleUploadImage = async (event) => {
        if (!event.target.files[0]) return;

        try {
            setIsLoading((prev) => ({ ...prev, icon: true }));
            const formData = new FormData();
            formData.append("image", event.target.files[0]);
            const response = await axios({
                method: "post",
                url: `${baseUrl}image/upload`,
                data: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });
            setInputError((pre) => ({ ...pre, icon: false }));
            setInputValues((pre) => ({ ...pre, icon: response?.data?.url }));
            handleOpenSnackBar("success", t("success.imageUploadSuccess", { ns: "common" }));
        } catch (error) {
            handleOpenSnackBar("error", t("error.imageUploadFailed", { ns: "common" }));
        } finally {
            openIconFile.current.value = null;
            setIsLoading((prev) => ({ ...prev, icon: false }));
        }
    };


    return (
        <>
            <AppLoader isVisible={isLoading.save} />
            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <DialogHeader
                    title={paramsData.type === "add"
                        ? t("addEcommerceTag")
                        : t("editEcommerceTag")}
                    secondaryHeading={paramsData.type === "add"
                        ? t("addEcommerceTagInstruction")
                        : t("editEcommerceTagInstruction")}
                    onClose={handleClose}
                    icon={<TagIcon />}
                />

                <DialogBody>
                    <Grid container spacing={2}>
                        {/* Small Icon */}
                        <Grid item xs={12}>
                            <UploadBox
                                inputRef={openIconFile}
                                label={t("image", { ns: "common" })}
                                // note={t("smallIconSizeNote", { ns: "common" })}
                                value={inputValues.icon}
                                error={inputError.icon}
                                loading={isLoading.icon}
                                placeholderImage={APP_IMAGES.uploadImage}
                                accept="image/png, image/jpeg"
                                height={{ xs: 200, md: 130 }}
                                previewSize={{ xs: 160, md: 180 }}
                                onClick={() => openIconFile.current.click()}
                                onChange={handleUploadImage}
                                onRemove={() =>
                                    setInputValues({ ...inputValues, icon: null })
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Label
                                required
                                labelText={t("order")}
                                htmlFor={"ecommerce-tags-order"}
                            />
                            <TextInput
                                id={"ecommerce-tags-order"}
                                styleProps={globalStyle.textField}
                                placeholder={t("enterOrder", { ns: "common" })}
                                required
                                name={"order"}
                                type={"number"}
                                value={inputValues.order}
                                onChange={handleChageInputs}
                                inputError={inputError.order}
                                helperText={t(inputError.order)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Label
                                required
                                labelText={t("solutionType")}
                                htmlFor={`ecommerce-tags-solution-type`}
                            />
                            <CustomDropDown
                                name="solutionType"
                                value={inputValues.solutionType}
                                onChange={handleChageInputs}
                                inputError={inputError.solutionType}
                                errorText={t("pleaseSelectSolutionType")}
                                multiple
                            >
                                <MenuItem value="" disabled>
                                    {t("selectSolutionType")}
                                </MenuItem>
                                {ECOMMERCE_SOLUTION_TYPE.map((item, index) => (
                                    <MenuItem key={index} value={item.value}>
                                        {item.label[currentLanguage]}
                                    </MenuItem>
                                ))}
                            </CustomDropDown>
                        </Grid>
                        {/* <Grid item xs={12}>
                            <Label
                                required
                                labelText={t("appliesTo")}
                                htmlFor={`ecommerce-tags-solution-type`}
                            />
                            <CustomDropDown
                                name="appliesTo"
                                value={inputValues.appliesTo}
                                onChange={handleChageInputs}
                                inputError={inputError.appliesTo}
                                errorText={t("pleaseSelectAppliesTo")}
                                
                            >
                                <MenuItem value="" disabled>
                                    {t("selectAppliesTo")}
                                </MenuItem>
                                {TAG_APPLIES_TO.map((item, index) => (
                                    <MenuItem key={index} value={item.value}>
                                        {item.label[currentLanguage]}
                                    </MenuItem>
                                ))}
                            </CustomDropDown>
                        </Grid> */}

                        <Grid item xs={12}>
                            <Label
                                required
                                labelText={t("tagNameInRomanian")}
                                htmlFor={`ecommerce-tags-name-ro`}
                            />
                            <TextInput
                                id={`ecommerce-tags-name-ro`}
                                required
                                placeholder={t("enterNameInRomanian")}
                                name={`name_ro`}
                                textLength={60}
                                value={inputValues.name_ro}
                                onChange={handleChageInputs}
                                inputError={Boolean(inputError.name_ro)}
                                helperText={t(inputError.name_ro)}
                                InputEndIcon={
                                    <Typography variant="body2" color="text.grey">
                                        {inputValues.name_ro?.length}/60
                                    </Typography>
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack
                                mb={"5px"}
                                direction={"row"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                            >
                                <Label
                                    required
                                    labelText={t("tagNameInEnglish")}
                                    htmlFor={`ecommerce-tags-name-en`}
                                    labelProps={{ marginBottom: 0 }}
                                />
                                <Chip
                                    label={t("translateToEnglish", { ns: "common" })}
                                    size="small"
                                    onClick={() => handleTranslateToEnglish("name_ro", "name_en")}
                                    icon={
                                        isLoading.translateToEnglish ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <TranslateIcon />
                                        )
                                    }
                                    disabled={isLoading.translateToEnglish}
                                />
                            </Stack>
                            <TextInput
                                id={`ecommerce-tags-name-en`}
                                required
                                placeholder={t("enterNameInEnglish")}
                                name={`name_en`}
                                textLength={60}
                                value={inputValues.name_en}
                                onChange={handleChageInputs}
                                inputError={Boolean(inputError.name_en)}
                                helperText={t(inputError.name_en)}
                                InputEndIcon={
                                    <Typography variant="body2" color="text.grey">
                                        {inputValues.name_en?.length}/60
                                    </Typography>
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Label
                                required
                                labelText={t(
                                    "tagDescriptionInRomanian",
                                )}
                                htmlFor={`ecommerce-tags-description-ro`}
                            />
                            <TextInput
                                id={`ecommerce-tags-description-ro`}
                                required
                                placeholder={t(
                                    "enterDescriptionInRomanian",
                                )}
                                name={`description_ro`}
                                textLength={255}
                                value={inputValues.description_ro}
                                onChange={handleChageInputs}
                                inputError={Boolean(inputError.description_ro)}
                                helperText={t(inputError.description_ro)}
                                multiline
                                rows={3}
                                InputEndIcon={
                                    <Typography variant="body2" color="text.grey">
                                        {inputValues.description_ro?.length}/255
                                    </Typography>
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack
                                mb={"5px"}
                                direction={"row"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                            >
                                <Label
                                    required
                                    labelText={t(
                                        "tagDescriptionInEnglish",
                                    )}
                                    htmlFor={`ecommerce-tags-description-en`}
                                    labelProps={{ marginBottom: 0 }}
                                />
                                <Chip
                                    label={t("translateToEnglish", { ns: "common" })}
                                    size="small"
                                    onClick={() =>
                                        handleTranslateToEnglish("description_ro", "description_en")
                                    }
                                    icon={
                                        isLoading.translateToEnglish ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <TranslateIcon />
                                        )
                                    }
                                    disabled={isLoading.translateToEnglish}
                                />
                            </Stack>
                            <TextInput
                                id={`ecommerce-tags-description-en`}
                                required
                                placeholder={t("enterDescriptionInEnglish")}
                                name={`description_en`}
                                textLength={255}
                                value={inputValues.description_en}
                                onChange={handleChageInputs}
                                inputError={Boolean(inputError.description_en)}
                                helperText={t(inputError.description_en)}
                                multiline
                                rows={3}
                                InputEndIcon={
                                    <Typography variant="body2" color="text.grey">
                                        {inputValues.description_en?.length}/255
                                    </Typography>
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogBody>
                <DialogActionButtons
                    onConfirm={handleAddEdit}
                    onCancel={handleClose}
                    confirmText={"save"}
                    cancelText={"cancel"}
                    isConfirmBtnDisable={isLoading.save}
                    showCancelButton={true}
                />
            </BootstrapDialog>
        </>
    );
});

export default AddEditEcommerceTagDialog;
